import { useMemo } from 'react';
import moment from 'moment';
import { Modal, ModalProps } from 'antd';
import { H5 } from 'components/ui/base/typography';
import useDeclarations from 'hooks/useDeclarations';
import { useRequestPromise } from 'hooks/useRequest';
import { lookupIEMRN } from 'store/declarations/client';

type Props = ModalProps;

const fmtDate = (d?: string) => (d ? moment(d, 'DD/MM/YYYY', true).format('dddd, D MMM YYYY') : '');

const fmtTime = (t?: string) => (t ? moment(t, 'HH:mm', true).format('HH:mm') : '');

// Combine date + time safely (local time)
const toMoment = (date?: string, time?: string) =>
    date ? moment(`${date} ${time ?? '00:00'}`, 'DD/MM/YYYY HH:mm', true) : null;

const MRNLookupModal = (props: Props) => {
    const { declaration } = useDeclarations();

    const {
        data: mrnLookup,
        isLoading,
        error,
    } = useRequestPromise(() => {
        if (!declaration?.id || !declaration?.mrn) return null;
        return lookupIEMRN(declaration?.id);
    });

    const enquiry = toMoment(mrnLookup?.dateOfEnquiry, mrnLookup?.timeOfEnquiry);
    const lastUpdate = toMoment(mrnLookup?.dateOfLastUpdate, mrnLookup?.timeOfLastUpdate);

    const enquiryDate = enquiry?.isValid() ? enquiry.format('dddd, D MMM YYYY') : fmtDate(mrnLookup?.dateOfEnquiry);
    const enquiryTime = enquiry?.isValid() ? enquiry.format('HH:mm') : fmtTime(mrnLookup?.timeOfEnquiry);
    const lastUpdateDate = lastUpdate?.isValid()
        ? lastUpdate.format('dddd, D MMM YYYY')
        : fmtDate(mrnLookup?.dateOfLastUpdate);
    const lastUpdateTime = lastUpdate?.isValid() ? lastUpdate.format('HH:mm') : fmtTime(mrnLookup?.timeOfLastUpdate);

    const content = useMemo(() => {
        if (error) return <p>Couldn't load MRN lookup data</p>;

        if (isLoading) return <p>Loading...</p>;

        if (!mrnLookup) return null;

        return (
            <div>
                <p>Our MRN: {declaration?.mrn}</p>
                <p>Actual MRN: {mrnLookup.mrn}</p>
                <p>Declaration Status: {mrnLookup.declarationStatus}</p>
                <p>Control Status: {mrnLookup.controlStatus}</p>
                <p>Date of Enquiry: {enquiryDate}</p>
                <p>Time of Enquiry: {enquiryTime}</p>
                <p>Date of Last Update: {lastUpdateDate}</p>
                <p>Time of Last Update: {lastUpdateTime}</p>
            </div>
        );
    }, [declaration?.mrn, enquiryDate, enquiryTime, error, isLoading, lastUpdateDate, lastUpdateTime, mrnLookup]);

    return (
        <Modal {...props} footer={null}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                <H5>MRN Lookup</H5>
                {content}
            </div>
        </Modal>
    );
};

export default MRNLookupModal;
